import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { apiGet } from "@/lib/api-client";
import { extractTwitterTimelineTweets, summarizeTwitterFeed } from "@/lib/twitter";

export function TwitterAnalysisPage() {
  const [query, setQuery] = useState("polymarket");
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [filter, setFilter] = useState<"all" | "influencer" | "trending">("all");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(timer);
  }, [query]);

  const normalizedQuery = debouncedQuery.trim() || "polymarket";

  const tweetsQuery = useQuery({
    queryKey: ["twitter", "tweets", normalizedQuery],
    queryFn: () =>
      apiGet<unknown>(
        `/api/twitter/search?query=${encodeURIComponent(normalizedQuery)}&count=50&type=Latest`,
      ),
    refetchInterval: 120_000,
  });

  const sentimentQuery = useQuery({
    queryKey: ["twitter", "sentiment", normalizedQuery],
    queryFn: () =>
      apiGet<unknown>(
        `/api/twitter/sentiment?market=${encodeURIComponent(normalizedQuery)}&count=50`,
      ),
    refetchInterval: 300_000,
  });

  const trendingQuery = useQuery({
    queryKey: ["twitter", "trending"],
    queryFn: () => apiGet<unknown>("/api/twitter/trending?location=worldwide"),
    refetchInterval: 600_000,
  });

  const baseTweets = useMemo(() => {
    const recentTweets = extractTwitterTimelineTweets(tweetsQuery.data);
    if (recentTweets.length > 0) {
      return recentTweets;
    }
    return extractTwitterTimelineTweets(sentimentQuery.data);
  }, [sentimentQuery.data, tweetsQuery.data]);

  const tweets = useMemo(() => {
    const trendingTweets = extractTwitterTimelineTweets(trendingQuery.data).map((tweet) => ({
      ...tweet,
      trending: true,
    }));
    const trendingIds = new Set(trendingTweets.map((tweet) => tweet.id));
    const seen = new Set(baseTweets.map((tweet) => tweet.id));
    const merged = baseTweets.map((tweet) =>
      trendingIds.has(tweet.id) ? { ...tweet, trending: true } : tweet,
    );

    for (const tweet of trendingTweets) {
      if (!seen.has(tweet.id)) {
        seen.add(tweet.id);
        merged.push(tweet);
      }
    }

    return merged;
  }, [baseTweets, trendingQuery.data]);

  const summary = useMemo(() => summarizeTwitterFeed(baseTweets), [baseTweets]);

  const positive = summary.positive;
  const negative = summary.negative;
  const neutral = summary.neutral;
  const totalTweets = summary.total;
  const hashtags = summary.hashtags;
  const influencers = summary.influencers;

  const filteredTweets = tweets.filter((tweet) => {
    if (filter === "influencer") return tweet.influencer;
    if (filter === "trending") return tweet.trending;
    return true;
  });

  const anyError = tweetsQuery.isError || sentimentQuery.isError || trendingQuery.isError;
  // Both tweet sources failed — the sentiment summary has no data behind it.
  const feedUnavailable = tweetsQuery.isError && sentimentQuery.isError;

  return (
    <div className="space-y-4 md:space-y-6">
      {anyError ? (
        <QueryErrorState
          what="Twitter data"
          onRetry={() => {
            if (tweetsQuery.isError) void tweetsQuery.refetch();
            if (sentimentQuery.isError) void sentimentQuery.refetch();
            if (trendingQuery.isError) void trendingQuery.refetch();
          }}
        />
      ) : null}

      <Panel
        title="Twitter Analysis"
        description="Social sentiment, influencer tracking, and trending hashtag analysis via the live Twitter RapidAPI routes."
        actions={
          <Input
            className="text-sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search topic"
          />
        }
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard label="Tweets Analyzed" value={feedUnavailable ? "—" : String(totalTweets)} hint="Sampled tweets" />
          <StatCard label="Positive %" value={feedUnavailable ? "—" : `${positive.toFixed(1)}%`} tone={!feedUnavailable && positive > 50 ? "success" : "default"} />
          <StatCard label="Negative %" value={feedUnavailable ? "—" : `${negative.toFixed(1)}%`} tone={!feedUnavailable && negative > 50 ? "warning" : "default"} />
          <StatCard label="Neutral %" value={feedUnavailable ? "—" : `${neutral.toFixed(1)}%`} />
          <StatCard label="Influencers" value={feedUnavailable ? "—" : String(influencers.length)} hint="Tracked accounts" />
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Sentiment Distribution" description="Tweet sentiment breakdown">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-green-400">Bullish</span>
                <span>{positive.toFixed(1)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gunmetal">
                <div className="h-full rounded-full bg-green-500" style={{ width: `${positive}%` }} />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Neutral</span>
                <span>{neutral.toFixed(1)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gunmetal">
                <div className="h-full rounded-full bg-slate-500" style={{ width: `${neutral}%` }} />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-red-400">Bearish</span>
                <span>{negative.toFixed(1)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gunmetal">
                <div className="h-full rounded-full bg-red-500" style={{ width: `${negative}%` }} />
              </div>
            </div>
          </div>
        </Panel>

        <Panel title="Trending Hashtags" description="Popular hashtags in tracked tweets">
          <div className="flex flex-wrap gap-2">
            {hashtags.map((tag, index) => (
              <span key={index} className="rounded-full bg-cyan-500/20 px-3 py-1 text-sm text-cyan-400">
                #{tag.tag}
              </span>
            ))}
            {hashtags.length === 0 && (
              <div className="w-full rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No trending hashtags data
              </div>
            )}
          </div>
        </Panel>
      </div>

      <Panel
        title="Recent Tweets"
        description="Latest analyzed tweets"
        actions={
          <Select className="text-sm" value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
            <option value="all">All Tweets</option>
            <option value="influencer">Influencers</option>
            <option value="trending">Trending</option>
          </Select>
        }
      >
        <div className="max-h-[400px] space-y-3 overflow-y-auto">
          {filteredTweets.length === 0 ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              {feedUnavailable ? "Tweet data unavailable" : "No tweets found"}
            </div>
          ) : (
            filteredTweets.slice(0, 20).map((tweet, index) => {
              const tweetSentiment = String(tweet.sentiment ?? "neutral").toLowerCase();
              const isPositive = tweetSentiment.includes("positive") || tweetSentiment.includes("bullish");
              const isNegative = tweetSentiment.includes("negative") || tweetSentiment.includes("bearish");

              return (
                <div key={index} className="rounded-lg bg-gunmetal/50 p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gunmetal">
                      {tweet.author_image ? (
                        <img src={String(tweet.author_image)} alt="" className="h-full w-full rounded-full object-cover" />
                      ) : (
                        <span className="text-lg">🐦</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{tweet.author || tweet.username || "Unknown"}</span>
                        {tweet.verified && <span className="text-blue-400">✓</span>}
                        <span className="text-xs text-slate-400">{tweet.date}</span>
                      </div>
                      <p className="mt-1 text-sm">{tweet.text}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-slate-400">
                        <span>♥ {tweet.likes.toLocaleString()}</span>
                        <span>🔄 {tweet.retweets.toLocaleString()}</span>
                        <span
                          className={`rounded-full px-2 py-0.5 ${
                            isPositive
                              ? "bg-green-500/20 text-green-400"
                              : isNegative
                                ? "bg-red-500/20 text-red-400"
                                : "bg-slate-500/20 text-slate-400"
                          }`}
                        >
                          {tweet.sentiment}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Panel>

      <Panel title="Top Influencers" description="Most impactful tracked accounts">
        <div className="max-h-[300px] space-y-2 overflow-y-auto">
          {influencers.length === 0 ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              No influencer data available
            </div>
          ) : (
            influencers.slice(0, 15).map((influencer, index) => (
              <div key={index} className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold-500/20 text-gold-400 font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium">{influencer.name || influencer.username || "Unknown"}</div>
                    <div className="text-xs text-slate-400">@{influencer.handle || influencer.username}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gold-400">{influencer.followers.toLocaleString()}</div>
                  <div className="text-xs text-slate-400">followers</div>
                </div>
              </div>
            ))
          )}
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Tweets API" description="Raw tweet data">
          <JsonView value={tweetsQuery.data} />
        </Panel>
        <Panel title="Trending API" description="Raw trending data">
          <JsonView value={trendingQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
